import React, { useState } from 'react';
import { identifyComponent } from '../../util';
import { TextInputBase, TextInputBaseProps } from './TextInputBase';
import { TextInputOutline, TextInputOutlineProps } from './TextInputOutline';
import { InputVariant } from './types';

export interface TextInputInnerProps
  extends TextInputBaseProps,
    Omit<TextInputOutlineProps, 'children'> {
  /**
   * A child element that will be displayed inside the left edge of the input. It is intended to be a button, or perhaps an icon.
   */
  leftAdornment?: React.ReactNode;
  /**
   * A child element that will be displayed inside the right edge of the input. It is intended to be a button, or perhaps an icon.
   */
  rightAdornment?: React.ReactNode;
}

export interface TextInputProps extends TextInputInnerProps {
  variant?: Exclude<InputVariant, 'multiselect'>;
}

const TextInputInner = React.forwardRef<HTMLInputElement, TextInputInnerProps>(
  (
    {
      disabled = false,
      fsMask,
      fullWidth = false,
      id,
      invalid = false,
      leftAdornment,
      placeholder,
      rightAdornment,
      size = 'medium',
      testId = 'text-input',
      variant = 'fill',
      onBlur,
      onFocus,
      className,
      ...rest
    },
    ref,
  ) => {
    const [focused, setFocused] = useState(false);

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(event);
      setFocused(false);
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      onFocus?.(event);
      setFocused(true);
    };

    return (
      <TextInputOutline
        data-component="TextInputInner"
        testId={`${testId}-wrapper`}
        className={className}
        size={size}
        disabled={disabled}
        focused={focused}
        invalid={invalid}
        fsMask={fsMask}
        fullWidth={fullWidth}
        variant={variant}
      >
        {leftAdornment}
        <TextInputBase
          aria-invalid={invalid}
          data-testid={testId}
          disabled={disabled}
          id={id}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={placeholder}
          ref={ref}
          {...rest}
          {...identifyComponent('text-input', rest)}
        />
        {rightAdornment}
      </TextInputOutline>
    );
  },
);
TextInputInner.displayName = 'TextInputInner';

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>((props, ref) => (
  <TextInputInner {...props} ref={ref} data-component="TextInput" />
));

TextInput.displayName = 'TextInput';

/**
 * TextInput's component parts are to be used when leftAdornment and rightAdornment props do not meet layout needs, such as with a multiline input.
 * Using these component parts allows for more flexibility in layout and styling,
 * but do not handle the focused state. Any implementation using these components
 * should handle focused state manually using the `onFocus` and `onBlur` props on InputBase and the focused prop on InputOutline.
 */
const TextInputInnerWithStatistics = TextInputInner as typeof TextInputInner & {
  Outline: typeof TextInputOutline;
  InputBase: typeof TextInputBase;
};

TextInputInnerWithStatistics.Outline = TextInputOutline;
TextInputInnerWithStatistics.InputBase = TextInputBase;

export { TextInputInnerWithStatistics as TextInputInner };
